import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import ErrorMessage from '@/components/common/ErrorMessage';
import FormHead from '@/components/common/Form/FormHead';
import FieldLayout from '@/components/Layout/FieldLayout';
import Button from '@/components/ui/Button';
import Label from '@/components/ui/Label';
import PrimaryInput from '@/components/ui/PrimaryInput';
import heic2any from 'heic2any';
import {
  IAddEmployeeForm,
  IEmployeeOnBoardResponse,
} from '@/interfaces/employee.model';
import { fetchUserDetails } from '@/redux/features/UserSlice';
import { AppDispatch, RootState } from '@/redux/store';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import {
  ChangeEvent,
  createRef,
  Dispatch,
  MouseEvent,
  useEffect,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
interface IEmployeeBasicDetailsStep1Props {
  setImageFile: Dispatch<React.SetStateAction<File | string | null>>;
  nextStep: () => void;
  imageFile: string | File | null;
  empId?: string | null;
  isProfile?: boolean;
}
const EmployeeBasicDetailsStep1 = ({
  setImageFile,
  nextStep,
  imageFile,
  empId,
  isProfile,
}: IEmployeeBasicDetailsStep1Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [imageAdding, setimageAdding] = useState<boolean>(false);
  const [image, setImage] = useState<string | null>(null);
  const fileInputRef = createRef<HTMLInputElement>();
  const userDetails = useSelector((state: RootState) => state?.user?.user);
  const dispatch = useDispatch<AppDispatch>();
  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
  // const skillType = ['Unskilled', 'Semi- Skilled', 'Skilled', 'Highly Skilled'];
  const {
    getValues,
    register,
    formState: { errors },
    trigger,
    watch,
    setValue,
  } = useFormContext<IAddEmployeeForm>();
  useEffect(() => {
    register('profilePhoto', { ...errorTypes.required('Profile photo') });
  }, [register]);
  useEffect(() => {
    if (typeof imageFile == 'string' && !image) {
      setImage(imageFile);
    }
  }, [imageFile]);
  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event?.target?.files) return;
    let file = event.target.files[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showToastNotification('danger', 'File size cannot exceed 10 MB');
        event.target.value = '';
        return;
      }

      const isHeicFile = (file: File) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ext === 'heic' || ext === 'heif';
      };
      if (isHeicFile(file)) {
        try {
          setimageAdding(true);
          const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9,
          });

          file = new File(
            [convertedBlob as Blob],
            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
            { type: 'image/jpeg' },
          );
        } catch (err) {
          console.log(err);
          showToastNotification('danger', 'Unable to preview HEIC image');
          return;
        } finally {
          setimageAdding(false);
        }
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            setImage(reader.result);
          }
          setImageFile(file);
          setValue('profilePhoto', file);
        };
        reader.readAsDataURL(file);
      }
    }
    const previewUrl = URL.createObjectURL(file);
    setImage(previewUrl);
    setImageFile(file);
    setValue('profilePhoto', file, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const handleRemoveImage = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setImage(null);
    setImageFile(null);
    setValue('profilePhoto', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleNextStep = async () => {
    const isValid = await trigger([
      'firstName',
      'lastName',
      'gender',
      'email',
      'phone',
      'dob',
      'fatherName',
      'motherName',
      'joiningDate',
      'bloodGroup',
      'isMarried',
      'employeeCode',
      'profilePhoto',
      'ecpName',
      'ecpPhone',
    ]);

    if (!image) {
      return showToastNotification('danger', 'please add your image');
    }
    const data = getValues();
    if (isValid && image) {
      setIsLoading(true);
      if (empId) {
        const formData = new FormData();

        formData.append(
          'name',
          (data?.firstName || '').trim() + ' ' + (data?.lastName || '').trim(),
        );
        formData.append('email', data?.email);
        formData.append('phone', data?.phone);
        formData.append('dob', data?.dob);
        formData.append('gender', String(data?.gender));
        formData.append('bloodGroup', data?.bloodGroup);
        formData.append('ecpName', data?.ecpName);
        formData.append('ecpPhone', data?.ecpPhone);
        if (imageFile) {
          if (typeof imageFile === 'string') {
            formData.append('profilePhoto', imageFile);
          } else {
            formData.append('profilePhoto', imageFile);
          }
        }
        formData.append('shiftId', data?.shiftId);
        formData.append('fatherName', data?.fatherName);
        formData.append('motherName', data?.motherName);
        formData.append('isMarried', data?.isMarried);
        formData.append('status', data?.status);
        formData.append('joiningDate', data?.joiningDate);
        formData.append('employeeCode', String(data?.employeeCode));
        try {
          const response: IEmployeeOnBoardResponse = await callApi(
            API_URL + API_ENDPOINT.addEmployee + `?userId=${empId}`,
            { method: 'POST', data: formData, multipart: true },
          );
          if (response?.status) {
            showToastNotification('success', response?.message);
            if (empId == userDetails?.id) {
              dispatch(fetchUserDetails());
            }
            nextStep();
          }
        } catch (error) {
          if (error && typeof error === 'object' && 'message' in error) {
            showToastNotification(
              'danger',
              (error as IApiErrorResponse).message,
            );
          } else {
            showToastNotification('danger', 'Something went wrong');
          }
        } finally {
          setIsLoading(false);
        }
      } else {
        nextStep();
      }
    }
  };

  return (
    <>
      <div className="row">
        <FormHead heading="Normal Details" />
        <div className="col-12">
          <div className="row">
            <div className="col-12 col-md-6 col-lg-3">
              <div className="mb-3">
                <Label
                  id="employeeImg"
                  labelClassname=" label-img position-relative"
                >
                  {imageAdding ? (
                    <Spinner />
                  ) : image ? (
                    <img
                      loading="lazy"
                      src={image}
                      alt="Preview"
                      className=""
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <img src={'/employee.svg'} alt="default" />
                  )}
                  {image && (
                    <button
                      className="btn btn-outline-danger rounded-circle position-absolute top-0 end-0 px-2 py-2"
                      type="button"
                      onClick={handleRemoveImage}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </Label>
                <input
                  ref={fileInputRef}
                  className="form-control d-none"
                  type="file"
                  // {...register('profilePhoto')}
                  accept="image/*"
                  id="employeeImg"
                  onChange={handleImageUpload}
                />
              </div>
            </div>
          </div>
        </div>
        <ErrorMessage message={errors?.profilePhoto?.message} />

        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.employeeCode?.message}
            label="Employee Code"
            required={true}
            type={'text'}
            // maxLength={3}
            disabled={Boolean(empId)}
            placeholder="Enter employee code"
            id="employeeCode"
            {...register('employeeCode', {
              ...errorTypes.required('Employee Code'),
              ...(!empId && {
                pattern: {
                  value: /^(?=.{1,3}$)(?!(0+)$)[A-Za-z0-9]+$/,
                  message: 'Invalid employee code',
                },
                maxLength: {
                  value: 3,
                  message: 'Employee code cannot exceed 3 characters',
                },
              }),
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.firstName?.message}
            label="First name"
            required={true}
            maxLength={160}
            placeholder="Enter first name"
            {...register('firstName', {
              ...errorTypes.required('First Name'),
              ...errorTypes.name,
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.lastName?.message}
            label="Last name"
            required={true}
            maxLength={160}
            placeholder="Enter last name"
            {...register('lastName', {
              ...errorTypes.required('Last Name'),
              ...errorTypes.name,
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <Label id="Role" required={true}>
            Gender
          </Label>
          <select
            className="form-select form-control"
            aria-label="Default select example"
            {...register('gender', {
              ...errorTypes.required('Gender'),
            })}
          >
            <option value="" className="d-none">
              Select gender
            </option>
            <option value={0}>Male</option>
            <option value={1}>Female</option>
          </select>
          <ErrorMessage message={errors?.gender?.message} />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.email?.message}
            label="Email"
            id={'email'}
            type="email"
            placeholder="Enter email"
            required={true}
            disabled={isProfile}
            {...register('email', {
              ...errorTypes.required('Email'),
              ...errorTypes.email,
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.phone?.message}
            label="Mobile No."
            id={'phone'}
            required={true}
            maxLength={10}
            placeholder="Enter mobile No."
            disabled={isProfile}
            {...register('phone', {
              ...errorTypes.required('Phone'),
              ...errorTypes.phone,
            })}
            onChange={(e) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, '');
            }}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.dob?.message}
            label="Date of Birth"
            required={true}
            maxLength={10}
            type="date"
            id="Datebirth"
            placeholder="**|**|****"
            max={new Date().toISOString().split('T')[0]}
            {...register('dob', {
              ...errorTypes.required('Date'),
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.fatherName?.message}
            label="Father name"
            required={true}
            type="text"
            placeholder="Enter father name"
            id="FatherName"
            aria-describedby="emailHelp"
            maxLength={160}
            {...register('fatherName', {
              ...errorTypes.required('Father Name'),
              ...errorTypes.name,
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.motherName?.message}
            label="Mother name"
            required={true}
            type="text"
            placeholder="Enter mother name"
            id="MotherName"
            maxLength={160}
            {...register('motherName', {
              ...errorTypes.required('Mother Name'),
              ...errorTypes.name,
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.ecpName?.message}
            label="Emergency Person"
            // required={true}
            maxLength={160}
            placeholder="Enter emergency person name"
            {...register('ecpName', {
              // ...errorTypes.required('Emergency Person'),
              ...errorTypes.name,
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.ecpPhone?.message}
            label="Emergency Contact"
            id={'ecpPhone'}
            // required={true}
            maxLength={10}
            placeholder="Enter emergency contact"
            {...register('ecpPhone', {
              // ...errorTypes.required('Emergency Contact'),
              ...errorTypes.phone,
            })}
            onChange={(e) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, '');
            }}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <PrimaryInput
            errors={errors?.joiningDate?.message}
            label="Joining Date"
            required={true}
            maxLength={10}
            type="date"
            disabled={isProfile}
            placeholder="**|**|****"
            max={new Date().toISOString().split('T')[0]}
            id="joinDate"
            {...register('joiningDate', {
              ...errorTypes.required('Joining Date'),
              validate: (joining) => {
                const dob = watch('dob');
                if (!dob) return true;
                return (
                  new Date(joining) >= new Date(dob) ||
                  'Joining Date cannot be earlier than Date of Birth'
                );
              },
            })}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-4">
          <Label id="bloodGroup" required={true}>
            Blood Group
          </Label>
          <select
            className="form-select form-control"
            aria-label="Default select example"
            {...register('bloodGroup', {
              ...errorTypes.required('Blood Group'),
            })}
          >
            <option value="" className="d-none">
              Select blood group
            </option>
            {bloodGroups?.map((grp) => (
              <option value={grp}>{grp}</option>
            ))}
          </select>
          <ErrorMessage message={errors?.bloodGroup?.message} />
        </FieldLayout>
        {!isProfile && (
          <FieldLayout fieldStyle="col-lg-4">
            <Label id="status" required={true}>
              Status
            </Label>
            <select
              className="form-select form-control"
              aria-label="Default select example"
              disabled={!empId}
              {...register('status')}
            >
              <option value="" className="d-none">
                Select status
              </option>
              <option value={'current'}>Current</option>
              <option value={'exEmployee'}>Ex-Employee</option>
              <option value={'transfer'}>Transfer</option>
            </select>
            <ErrorMessage message={errors?.status?.message} />
          </FieldLayout>
        )}
        <FieldLayout fieldStyle="col-lg-4">
          <Label id="isMarried">Marital Status</Label>
          <select
            className="form-select form-control"
            aria-label="Default select example"
            {...register('isMarried', {
              ...errorTypes.required('Marital Status'),
            })}
          >
            <option value="" className="d-none">
              Select marital status
            </option>
            <option value="true">Married</option>
            <option value="false">Unmarried</option>
          </select>
          <ErrorMessage message={errors?.isMarried?.message} />
        </FieldLayout>
      </div>
      <div className="d-flex align-items-center justify-content-end">
        <Button
          className="btn btn-primary px-4"
          type="button"
          isLoading={isLoading}
          onClick={handleNextStep}
        >
          {empId ? 'Update and Continue' : 'Save and Continue'}
        </Button>
      </div>
    </>
  );
};
export default EmployeeBasicDetailsStep1;
